/* eslint-disable no-param-reassign */
import {
  ActionReducerMapBuilder,
  createAsyncThunk,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';

import { filterType } from '../../config/enum';
import DefaultApiParameters from '../../enum/DefaultApiParameters';
import {
  APIConsumerTransactionHistoryResponseInterface,
  ConsumerTransactionHistoryInitialProperties,
  RequestConsumerTransactionHistoryInterFace,
} from '../../interface/end-consumer-transaction-history-interface';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import {
  ClearFilters,
  ConfigureListFilterResponse,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
} from '../../shared-functions/CommonFilters';
import { removeEmptyKeys } from '../../shared-functions/DateTime';
import emspTransactionApi from '../../utils/api/v2/emsp-transaction';

import { setNotificationMessage } from './NotificationManagementSlice';

export const fetchConsumerTransactionHistory = createAsyncThunk<
  APIConsumerTransactionHistoryResponseInterface,
  RequestConsumerTransactionHistoryInterFace
>(
  'end-consumers/fetchConsumerTransactionHistory',
  async (data: RequestConsumerTransactionHistoryInterFace, { dispatch }) => {
    const { userCustomId, searchBy, ...query } = data;
    if (!userCustomId) {
      throw new Error('userCustomId is required');
    }
    if (searchBy) {
      query.search = searchBy;
    }
    try {
      const response = await emspTransactionApi.fetchCommercialTransactionList({
        userCustomId,
        ...removeEmptyKeys(query),
      });
      return {
        ...response.data,
        results: response.data.results.map((transaction: any) => ({
          ...transaction,
          status: transaction.paymentStatus,
          chargingTransactionId:
            transaction.externalId || transaction.sessionId || transaction._id,
          paymentMethod:
            transaction?.paymentMethod === 'hyperpay'
              ? 'Credit Card'
              : transaction?.paymentMethod,
        })),
      };
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          type: 'error',
          message: error?.response?.data?.message,
          code: error?.response?.data?.code,
          status: true,
        }),
      );
      throw error;
    }
  },
);

export const fetchConsumerTransactionHistoryFilter = createAsyncThunk<
  CommonFilterProperties[],
  void
>('end-consumers/fetchConsumerTransactionHistoryFilter', async (_, { dispatch, getState }) => {
  const state: any = getState();
  const { selectedEndConsumer } = state.endConsumerType;

  if (!selectedEndConsumer?.userCustomId) {
    throw new Error('accountCustomId is required');
  }
  try {
    const response = await emspTransactionApi.fetchCommercialTransactionListFilters();
    const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(response.data);
    return updatedResponse;
  } catch (error: any) {
    dispatch({
      type: 'notification/setNotificationMessage',
      payload: {
        type: 'error',
        message: error?.response?.data?.message || 200,
        code: error?.response?.data?.code || 'Something went wrong!',
        status: true,
      },
    });
    throw error;
  }
});

const availableFilters: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

// Initial state
const initialState: ConsumerTransactionHistoryInitialProperties = {
  consumers: [],
  limit: DefaultApiParameters.limit,
  page: DefaultApiParameters.page,
  totalPages: 0,
  totalResults: 0,
  loading: false,
  filter: {},
  filters: availableFilters,
  filterListLoading: false,
  isFilterUpdated: false,
  sortBy: null,
  sortField: null,
};

export const ConsumerTransactionHistory = createSlice({
  name: 'end-consumers-transaction-history',
  initialState,
  reducers: {
    /**
     * Update By month filters
     * @param {any} state - The current state.
     * @param {any} action - The action object.
     */
    updateConsumerTransactionHistoryListFilter: (
      state,
      action: PayloadAction<FilterUpdateProperties>,
    ) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
        state.sortBy = null;
        state.sortField = null;
      }
    },
    /**
     * function for set sortBy
     * @param {any} state - The current state.
     * @param {any} action - The action object.
     */
    setSortData: (state, action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>) => {
      state.sortField = action.payload.sortField;
      state.sortBy = action.payload.sortBy;
    },
  },
  /**
   * Use the extra reducers function
   * @param {any} builder - The builder object.
   */
  extraReducers: (
    builder: ActionReducerMapBuilder<ConsumerTransactionHistoryInitialProperties>,
  ) => {
    builder
      .addCase(
        fetchConsumerTransactionHistory.pending,
        (state: ConsumerTransactionHistoryInitialProperties) => {
          state.loading = true;
        },
      )
      .addCase(
        fetchConsumerTransactionHistory.fulfilled,
        (
          state: ConsumerTransactionHistoryInitialProperties,
          action: PayloadAction<APIConsumerTransactionHistoryResponseInterface>,
        ) => {
          state.loading = false;
          state.consumers = [...action.payload.results];
          state.limit = action.payload.limit;
          state.page = action.payload.page;
          state.totalPages = action.payload.totalPages;
          state.totalResults = action.payload.totalResults;
        },
      )
      .addCase(
        fetchConsumerTransactionHistory.rejected,
        (state: ConsumerTransactionHistoryInitialProperties) => {
          state.loading = false;
          // Handle error if needed
        },
      );

    builder
      .addCase(fetchConsumerTransactionHistoryFilter.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(
        fetchConsumerTransactionHistoryFilter.fulfilled,
        (state, action: PayloadAction<CommonFilterProperties[]>) => {
          state.filterListLoading = false;
          state.filters = [...(state.filters || []), ...action.payload];
        },
      )
      .addCase(fetchConsumerTransactionHistoryFilter.rejected, (state) => {
        state.filterListLoading = false;
      });
  },
});

// Export actions
export const { updateConsumerTransactionHistoryListFilter, setSortData } =
  ConsumerTransactionHistory.actions;
// Export the reducer
export default ConsumerTransactionHistory.reducer;
